import React from "react";
import { useHistory } from "react-router-dom";
import clsx from "clsx";

import format from "date-fns/format";
import PropTypes from "prop-types";

// Material
import { makeStyles } from "@material-ui/core/styles";
import ButtonBase from "@material-ui/core/ButtonBase";
import Paper from "@material-ui/core/Paper";

// Config
import colors from "../../config/colors";
import { getRoute, urls } from "../../config/routes";
import styles from "../../config/styles";

// Icons
import chevron from "../../assets/icons/chevron.svg";
import clock from "../../assets/icons/clock.svg";
import locationIcon from "../../assets/icons/location_ico.svg";

// Utils
import { EVIDENCE_SCORES, getEvidenceScore } from "../../utils/evidenceScore";
import { useCaseDetailStatus } from "src/context/CaseDetailStatusContext";

// Hooks
import useIncidentTypes from "../../hooks/useIncidentTypes";
import useLogs from "../../hooks/useLogs";
import useTranslate from "../../hooks/useTranslate";

// Components
import Button from "../Button";
import CaseLog from "./CaseLog";
import CaseLogPopper from "../CaseLogPopper";
import ContentCardHeader from "../ContentCardHeader";
import EvidenceScoreBar from "../EvidenceScoreBar";
import LightTooltip from "../LightTooltip";
import LoadingSection from "../LoadingSection";
import MiniMap from "../MiniMap";
import PopOver from "../PopOver";
import StatusChip from "../StatusChip";
import Text from "../Text";

const useStyles = makeStyles(theme => ({
  root: {
    border: styles.values.border
  },
  titleWrapper: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2, 0)
  },
  title: {
    textTransform: "capitalize",
    fontSize: 24
  },
  titleValue: {
    fontSize: 14
  },
  chevron: {
    transform: "rotate(180deg)",
    marginLeft: theme.spacing(0.5)
  },
  updated: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center"
  },
  date: {
    margin: theme.spacing(0, 0.5)
  },
  contentContainer: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(5, 4)
  },
  contentWrapper: {
    display: "flex",
    flexDirection: "column",
    borderBottom: styles.values.border,
    gap: theme.spacing(1),
    "&:last-of-type": {
      border: "none"
    }
  },
  valuesWrapper: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    height: 24
  },
  chip: {
    borderRadius: theme.shape.borderRadius,
    marginLeft: "auto",
    height: 20
  },
  valueText: {
    color: colors.riverBed,
    fontFamily: ["Open Sans", "sans-serif"].join(",")
  },
  locationValueText: {
    color: colors.pictonBlue,
    fontFamily: ["Open Sans", "sans-serif"].join(","),
    textDecoration: "underline",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  locationValueWrapper: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(0.5),
    minWidth: 0,
    maxWidth: 450
  },
  locationLabel: {
    flexShrink: 0
  },
  locationClickable: {
    cursor: "pointer"
  },
  mapWrapper: {
    width: "300px"
  },
  shareButton: {
    backgroundColor: colors.emergencyHeader,
    height: 30,
    padding: theme.spacing(0, 2),
    textTransform: "capitalize",
    lineHeight: 1
  }
}));

const CaseInfoCard = ({ data }) => {
  const [logs, loading] = useLogs(5);
  const { data: incidentTypes } = useIncidentTypes();
  const getText = useTranslate();
  const history = useHistory();
  const { isLoadingStale } = useCaseDetailStatus();

  const incident = data.incident || {};
  const incidentTypeName = incidentTypes?.find(
    item => item.id === incident.incidentTypeId
  )?.name;

  const incidentDetailsCompleted =
    data.completedSections?.includes("IncidentDetails") ?? false;
  const evidenceStored = data.numberOfMediaItems || 0;
  const score = getEvidenceScore(incidentDetailsCompleted, evidenceStored);
  const classes = useStyles();
  const isShareState =
    score === EVIDENCE_SCORES.SOME_EVIDENCE ||
    score === EVIDENCE_SCORES.LOTS_OF_EVIDENCE;

  const handleClickProgress = () => {
    if (isShareState) {
      history.push(getRoute(urls.shareWith, { caseId: data.id }));
    } else if (!incidentDetailsCompleted) {
      history.push(getRoute(urls.incident, { caseId: data.id }));
    } else {
      history.push(getRoute(urls.caseMedia, { caseId: data.id }));
    }
  };

  if (isLoadingStale) {
    return (
      <Paper elevation={0} className={classes.root}>
        <ContentCardHeader>
          <div className={classes.titleWrapper}>
            <Text
              type="heading2"
              className={classes.title}
              text="caseMenu.caseSummary.title"
            />
          </div>
        </ContentCardHeader>
        <div className={classes.content}>
          <LoadingSection normalHeight noBackground />
        </div>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} className={classes.root}>
      <ContentCardHeader>
        <div className={classes.titleWrapper}>
          <Text
            type="heading2"
            className={classes.title}
            text="caseMenu.caseSummary.title"
          />
        </div>

        <LightTooltip
          placement="bottom-end"
          arrow
          interactive
          maxWidth={500}
          padding={0}
          title={<CaseLogPopper loading={loading} logs={logs} />}
        >
          <div className={classes.updated}>
            <img src={clock} alt="" />
            <Text
              className={classes.date}
              text="caseDetailPage.caseInfoCard.lastUpdatedLabel"
              type="body"
            />
            <Text type="contentSmall" className={classes.dateValue}>
              {format(
                new Date(data.updateDateTime || data.creationDateTime),
                "dd/MM/yyyy"
              )}
            </Text>
            <img src={chevron} alt="" className={classes.chevron} />
          </div>
        </LightTooltip>
      </ContentCardHeader>
      {data.isDraft && data.isOwnedByUser && (
        <EvidenceScoreBar score={score} isIntel={data.isIntel}>
          <Button
            variant="contained"
            regular
            disableElevation
            color="primary"
            className={classes.shareButton}
            onClick={handleClickProgress}
          >
            {getText(
              isShareState ? "common.prompts.share" : "common.prompts.ok"
            )}
          </Button>
        </EvidenceScoreBar>
      )}
      <div className={classes.contentContainer}>
        <div className={classes.contentWrapper}>
          <div className={classes.valuesWrapper}>
            <Text
              text="common.valueLabels.status"
              type="contentLabel3"
              className={classes.valueText}
            />
            {!data.submissions || data.submissions.length === 0 ? (
              <Text type="status" className={classes.valueText}>
                {getText("common.prompts.none")}
              </Text>
            ) : (
              <StatusChip submissions={data.submissions} />
            )}
          </div>
          <div className={classes.valuesWrapper}>
            <Text
              text="caseIncidentsPage.incidentTypeLabel"
              type="contentLabel3"
              className={classes.valueText}
            />
            <Text type="status" className={classes.valueText}>
              {incidentTypeName || getText("common.prompts.none")}
            </Text>
          </div>
          <div className={classes.valuesWrapper}>
            <Text
              text="common.valueLabels.incidentDate"
              type="contentLabel3"
              className={classes.valueText}
            />
            <Text type="status" className={classes.valueText}>
              {incident.dateTime
                ? format(new Date(incident.dateTime), "dd/MM/yyyy")
                : getText("common.prompts.none")}
            </Text>
          </div>
          <div className={classes.valuesWrapper}>
            <Text
              text="common.valueLabels.location"
              type="contentLabel3"
              className={classes.valueText}
            />
            {incident.location?.coordinate ? (
              <PopOver
                render={({ onClick }) => (
                  <ButtonBase
                    className={clsx(
                      classes.locationValueWrapper,
                      classes.locationClickable
                    )}
                    onClick={onClick}
                  >
                    <img src={locationIcon} alt="" />
                    <Text type="status" className={classes.locationValueText}>
                      {incident.location.name}
                    </Text>
                  </ButtonBase>
                )}
              >
                <div className={classes.mapWrapper}>
                  <MiniMap
                    latitude={incident.location.coordinate.latitude}
                    longitude={incident.location.coordinate.longitude}
                  />
                </div>
              </PopOver>
            ) : (
              <div className={classes.locationValueWrapper}>
                <img src={locationIcon} alt="" />
                <Text type="status" className={classes.locationValueText}>
                  {incident.location?.name || getText("common.prompts.none")}
                </Text>
              </div>
            )}
          </div>
          <div className={classes.valuesWrapper}>
            <Text
              text="common.valueLabels.evidenceCaptured"
              type="contentLabel3"
              className={classes.valueText}
            />
            <Text type="status" className={classes.valueText}>
              {data.numberOfMediaItems || getText("common.prompts.none")}
            </Text>
          </div>
        </div>
        {data.userCapacity === "PoliceOfficer" && (
          <div className={classes.contentWrapper}>
            <CaseLog caseId={data.id} />
          </div>
        )}
      </div>
    </Paper>
  );
};

CaseInfoCard.propTypes = {
  data: PropTypes.shape({
    completedSections: PropTypes.arrayOf(PropTypes.string),
    creationDateTime: PropTypes.string,
    id: PropTypes.string,
    isDraft: PropTypes.bool,
    isIntel: PropTypes.bool,
    isOwnedByUser: PropTypes.bool,
    incident: PropTypes.shape({
      dateTime: PropTypes.string,
      incidentTypeId: PropTypes.string,
      location: PropTypes.shape({
        name: PropTypes.string,
        coordinate: PropTypes.shape({
          latitude: PropTypes.number,
          longitude: PropTypes.number
        })
      })
    }),
    number: PropTypes.string,
    numberOfMediaItems: PropTypes.number,
    submissions: PropTypes.arrayOf(PropTypes.shape({})),
    updateDateTime: PropTypes.string,
    userCapacity: PropTypes.string
  }).isRequired
};

export default CaseInfoCard;
